import { useMemo } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkBreaks from 'remark-breaks'

import { cn } from '~/DesignSystem/lib/utils'

type EventTextProps = React.ComponentProps<typeof ReactMarkdown>

const DEFAULT_CLASSES = 'break-words'
const DEFAULT_MARGIN = 'my-4'
const MARGIN_CONTROL = 'break-words first:mt-0 last:mb-0'

type MessageTextParagraphProps = React.ComponentProps<'p'>

export function MessageTextParagraph({ className, ...props }: MessageTextParagraphProps) {
  return (
    <p
      className={cn(DEFAULT_CLASSES, DEFAULT_MARGIN, MARGIN_CONTROL, className, '[li_>_&]:my-2')}
      {...props}
    />
  )
}

function MessageText({
  remarkPlugins: inRemarkPlugins,
  components: inComponents,
  children,
  ...props
}: EventTextProps) {
  const remarkPlugins = useMemo(() => {
    return (inRemarkPlugins ? inRemarkPlugins : []).concat(remarkBreaks)
  }, [inRemarkPlugins])

  return (
    <ReactMarkdown
      {...props}
      remarkPlugins={remarkPlugins}
      components={{
        ...inComponents,
        // Inline
        strong: props => <strong {...props} className={cn(DEFAULT_CLASSES, 'font-semibold')} />,
        em: props => <em {...props} className={cn(DEFAULT_CLASSES, 'italic')} />,
        del: props => <del {...props} className={cn(DEFAULT_CLASSES, 'line-through')} />,
        code: props => (
          <code {...props} className={cn(DEFAULT_CLASSES, 'bg-gray-100 px-1 rounded')} />
        ),
        a: ({ target = '_parent', className, ...props }) => (
          <a
            target={target}
            className={cn(DEFAULT_CLASSES, 'text-primary underline', className)}
            {...props}
          />
        ),
        // Bloques
        p: props => <MessageTextParagraph {...props} />,
        h1: props => (
          <h1
            {...props}
            className={cn(
              DEFAULT_CLASSES,
              'text-2xl font-semibold',
              DEFAULT_MARGIN,
              MARGIN_CONTROL,
            )}
          />
        ),
        h2: props => (
          <h2
            {...props}
            className={cn(DEFAULT_CLASSES, 'text-xl font-semibold', DEFAULT_MARGIN, MARGIN_CONTROL)}
          />
        ),
        ul: props => (
          <ul
            {...props}
            className={cn(
              DEFAULT_CLASSES,
              'list-disc ml-4',
              DEFAULT_MARGIN,
              MARGIN_CONTROL,
              '[li_>_&]:my-2',
            )}
          />
        ),
        ol: props => (
          <ol
            {...props}
            className={cn(DEFAULT_CLASSES, 'list-decimal ml-4', DEFAULT_MARGIN, MARGIN_CONTROL)}
          />
        ),
        li: props => <li {...props} className={cn(DEFAULT_CLASSES, 'my-2 ', MARGIN_CONTROL)} />,

        blockquote: props => (
          <blockquote
            {...props}
            className={cn(
              DEFAULT_CLASSES,
              'border-l-4 pl-4 italic',
              DEFAULT_MARGIN,
              MARGIN_CONTROL,
            )}
          />
        ),
        hr: props => (
          <hr {...props} className={cn(DEFAULT_CLASSES, DEFAULT_MARGIN, MARGIN_CONTROL)} />
        ),
      }}
    >
      {children}
    </ReactMarkdown>
  )
}

export default MessageText
